import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    Alert,
    Button,
    Card,
    Col,
    Empty,
    Form,
    Row,
    Select,
    Space,
    Tag,
    Tooltip,
    Typography,
    message,
} from "antd";
import {ClockCircleOutlined, DeleteOutlined, PlusOutlined, WarningOutlined} from "@ant-design/icons";
import {useQueryClient} from "@tanstack/react-query";
import {useSubscription} from "react-stomp-hooks";
import {SCHEDULES_KEY, useSaveSchedule, useSchedules} from "./shifts-query";

const {Text, Paragraph} = Typography;

const DAYS = [
    {value: "SATURDAY", label: "شنبه"},
    {value: "SUNDAY", label: "یک‌شنبه"},
    {value: "MONDAY", label: "دوشنبه"},
    {value: "TUESDAY", label: "سه‌شنبه"},
    {value: "WEDNESDAY", label: "چهارشنبه"},
    {value: "THURSDAY", label: "پنج‌شنبه"},
    {value: "FRIDAY", label: "جمعه"},
];
const DAY_LABEL = Object.fromEntries(DAYS.map((d) => [d.value, d.label]));
const DAY_ORDER = Object.fromEntries(DAYS.map((d, i) => [d.value, i]));

const HOURS = Array.from({length: 24}, (_, h) => ({value: h, label: String(h).padStart(2, "0")}));
// 59 is included deliberately: every shift on this desk ends at 23:59, and a picker that stops at
// 55 cannot express the convention the rota is already written in.
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 59].map((m) => ({
    value: m,
    label: String(m).padStart(2, "0"),
}));

const two = (n) => String(n ?? 0).padStart(2, "0");
const rangeLabel = (s) => `${two(s.startHour)}:${two(s.startMinute)} تا ${two(s.endHour)}:${two(s.endMinute)}`;

/**
 * The shift rota — who is on the desk, and when.
 *
 * <p>A screen of its own rather than a role-gated variant of the operator screen. That screen offers
 * creating an operator, deleting one, setting a password and editing an email, spread across a
 * desktop table and a separate mobile card tree; making each of those conditional on the viewer's
 * role would be several places to get right and to keep right, and one missed conditional there is a
 * privilege escalation. One missed conditional here is nothing, because there is nothing else here.
 *
 * <p>The real boundary is the backend: these endpoints are the only operator-facing write a
 * supervisor is granted. This component only avoids showing doors that would answer 403.
 */
export default function ShiftsPage() {
    const {data, isLoading, isError} = useSchedules();
    const queryClient = useQueryClient();
    const rows = useMemo(() => data?.data || data || [], [data]);

    const [editing, setEditing] = useState(null);   // operator id being edited
    const [form] = Form.useForm();

    /*
     * Live sync, so an admin and a supervisor cannot overwrite each other from a stale week.
     *
     * A literal per-user destination — Spring routes it to this session alone, so nobody can
     * subscribe to anybody else's feed, and the interceptor only accepts it from a principal the
     * oversight check already passed. Never a computed/nullable destination: useSubscription wraps a
     * non-array argument as [value], so passing null once made the client subscribe to the literal
     * string "null", and a refused SUBSCRIBE used to tear down the whole session.
     */
    useSubscription("/user/queue/operator/schedule", (frame) => {
        try {
            const payload = JSON.parse(frame.body);
            queryClient.setQueryData(SCHEDULES_KEY, payload);
            // The admin's operator screen renders the same rota from its own query.
            queryClient.invalidateQueries({queryKey: ["operators"]});
        } catch (e) {
            console.error("Could not read the rota push", e);
        }
    });

    const {mutate: save, isPending: saving} = useSaveSchedule(() => {
        message.success("برنامهٔ کاری ذخیره شد.");
        setEditing(null);
    });

    // Seed the form when an editor OPENS, and never again while it is open.
    //
    // `rows` was in the dependency list, so a rota push arriving mid-edit — which is exactly what
    // happens when the admin saves someone else at that moment — re-seeded the form underneath the
    // person typing and silently discarded their unsaved shifts. The ref makes the seed happen once
    // per opening; the list behind the editor still updates live.
    const seededFor = useRef(null);
    useEffect(() => {
        if (!editing) {
            seededFor.current = null;
            return;
        }
        if (seededFor.current === editing) return;
        seededFor.current = editing;
        const row = rows.find((r) => r.id === editing);
        form.setFieldsValue({
            workSchedule: (row?.workSchedule || []).map((s) => ({...s})),
        });
    }, [editing, rows, form]);

    // The backend's own message is the useful one — it names which rule was broken ("پایان بازه باید
    // بعد از شروع آن باشد", "هم‌پوشانی", "حداکثر ۲۱ بازه"). A generic failure would hide all of them.
    const reportFailure = (err) =>
        message.error(
            err?.response?.data?.message || err?.response?.data?.error || "ذخیره نشد.",
            6
        );

    const submit = (operatorId) => (vals) =>
        save(
            {
                operatorId,
                workSchedule: (vals.workSchedule || []).map((s) => ({
                    day: s.day,
                    startHour: s.startHour ?? 0,
                    startMinute: s.startMinute ?? 0,
                    endHour: s.endHour ?? 0,
                    endMinute: s.endMinute ?? 0,
                })),
            },
            {onError: reportFailure}
        );

    const sortedShifts = (list) =>
        [...(list || [])].sort(
            (a, b) =>
                (DAY_ORDER[a.day] ?? 99) - (DAY_ORDER[b.day] ?? 99) ||
                (a.startHour ?? 0) - (b.startHour ?? 0)
        );

    if (isError) {
        return <Alert type="error" showIcon message="برنامهٔ کاری بارگذاری نشد."/>;
    }

    return (
        <Card
            size="small"
            loading={isLoading}
            title={
                <Space>
                    <ClockCircleOutlined/>
                    <span>برنامهٔ کاری اپراتورها</span>
                </Space>
            }
        >
            <Paragraph type="secondary" style={{fontSize: 12, marginBottom: 16}}>
                این برنامه تعیین می‌کند گفت‌وگوی تازه به چه کسی سپرده شود. تغییر آن بلافاصله اثر
                می‌کند — نه در شیفت بعدی. پایان یک شیفت، گفت‌وگوهایی را که همان اپراتور همین حالا در
                دست دارد پس نمی‌گیرد؛ فقط کار تازه به او داده نمی‌شود.
            </Paragraph>
            <Alert
                type="info"
                showIcon
                style={{marginBottom: 16}}
                message="این صفحه ساعت پاسخ‌گویی اپراتورها را تعیین می‌کند، نه پیام «خارج از ساعت کاری» که مشتری می‌بیند."
            />

            {rows.length === 0 ? (
                <Empty description="اپراتوری تعریف نشده" image={Empty.PRESENTED_IMAGE_SIMPLE}/>
            ) : (
                rows.map((r) => {
                    const isEditing = editing === r.id;
                    const shifts = sortedShifts(r.workSchedule);
                    return (
                        <Card
                            key={r.id}
                            size="small"
                            style={{marginBottom: 12}}
                            styles={{body: {padding: 12}}}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 8,
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <div style={{minWidth: 0}}>
                                    <Space size={6} wrap>
                                        <Text strong>{r.fullName || "—"}</Text>
                                        {r.canSignIn === false && (
                                            <Tooltip title="این اپراتور حساب ورود ندارد، پس هیچ گفت‌وگویی به او سپرده نمی‌شود — هر برنامه‌ای هم که برایش تعریف شود بی‌اثر است. ساختن حساب کار مدیر است.">
                                                <Tag color="error" icon={<WarningOutlined/>} style={{fontSize: 11}}>
                                                    بدون حساب ورود
                                                </Tag>
                                            </Tooltip>
                                        )}
                                    </Space>
                                    <div>
                                        <Text type="secondary" style={{fontSize: 12}} dir="ltr">
                                            {r.email}
                                        </Text>
                                    </div>
                                </div>
                                {!isEditing && (
                                    <Button size="small" onClick={() => setEditing(r.id)}>
                                        ویرایش برنامه
                                    </Button>
                                )}
                            </div>

                            {!isEditing && (
                                <div style={{marginTop: 8}}>
                                    {shifts.length === 0 ? (
                                        <Tag color="warning">برنامهٔ کاری ندارد — گفت‌وگویی به او سپرده نمی‌شود</Tag>
                                    ) : (
                                        <Space size={[4, 4]} wrap>
                                            {shifts.map((s, i) => (
                                                <Tag key={i} color="processing" style={{fontSize: 11}}>
                                                    {DAY_LABEL[s.day] || s.day} — {rangeLabel(s)}
                                                </Tag>
                                            ))}
                                        </Space>
                                    )}
                                </div>
                            )}

                            {isEditing && (
                                <Form
                                    form={form}
                                    layout="vertical"
                                    requiredMark={false}
                                    onFinish={submit(r.id)}
                                    style={{marginTop: 12}}
                                >
                                    <Form.List name="workSchedule">
                                        {(fields, {add, remove}) => (
                                            <div>
                                                {fields.length === 0 && (
                                                    <Paragraph type="secondary" style={{fontSize: 12}}>
                                                        هیچ بازه‌ای تعریف نشده. ذخیره در این حالت یعنی این اپراتور
                                                        هیچ گفت‌وگوی تازه‌ای نمی‌گیرد.
                                                    </Paragraph>
                                                )}
                                                {fields.map((field, index) => (
                                                    <Row
                                                        key={field.key}
                                                        gutter={8}
                                                        align="middle"
                                                        style={{marginBottom: 4}}
                                                    >
                                                        <Col xs={24} md={7}>
                                                            <Form.Item
                                                                {...field}
                                                                name={[field.name, "day"]}
                                                                label={index === 0 ? "روز" : null}
                                                                rules={[{required: true, message: "روز را انتخاب کنید"}]}
                                                                style={{marginBottom: 8}}
                                                            >
                                                                <Select
                                                                    options={DAYS}
                                                                    placeholder="روز"
                                                                    getPopupContainer={(n) => n.parentElement}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={6} md={4}>
                                                            <Form.Item
                                                                {...field}
                                                                name={[field.name, "startHour"]}
                                                                label={index === 0 ? "از ساعت" : null}
                                                                rules={[{required: true, message: "لازم است"}]}
                                                                style={{marginBottom: 8}}
                                                            >
                                                                <Select options={HOURS}
                                                                        getPopupContainer={(n) => n.parentElement}/>
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={6} md={3}>
                                                            <Form.Item
                                                                {...field}
                                                                name={[field.name, "startMinute"]}
                                                                label={index === 0 ? "دقیقه" : null}
                                                                rules={[{required: true, message: "لازم است"}]}
                                                                style={{marginBottom: 8}}
                                                            >
                                                                <Select options={MINUTES}
                                                                        getPopupContainer={(n) => n.parentElement}/>
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={6} md={4}>
                                                            <Form.Item
                                                                {...field}
                                                                name={[field.name, "endHour"]}
                                                                label={index === 0 ? "تا ساعت" : null}
                                                                rules={[{required: true, message: "لازم است"}]}
                                                                style={{marginBottom: 8}}
                                                            >
                                                                <Select options={HOURS}
                                                                        getPopupContainer={(n) => n.parentElement}/>
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={6} md={3}>
                                                            <Form.Item
                                                                {...field}
                                                                name={[field.name, "endMinute"]}
                                                                label={index === 0 ? "دقیقه" : null}
                                                                rules={[{required: true, message: "لازم است"}]}
                                                                style={{marginBottom: 8}}
                                                            >
                                                                <Select options={MINUTES}
                                                                        getPopupContainer={(n) => n.parentElement}/>
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={24} md={3}>
                                                            <Button
                                                                danger
                                                                type="text"
                                                                size="small"
                                                                icon={<DeleteOutlined/>}
                                                                onClick={() => remove(field.name)}
                                                            >
                                                                حذف
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                ))}
                                                <Button
                                                    type="dashed"
                                                    size="small"
                                                    icon={<PlusOutlined/>}
                                                    onClick={() =>
                                                        add({
                                                            day: "SATURDAY",
                                                            startHour: 8,
                                                            startMinute: 0,
                                                            endHour: 16,
                                                            endMinute: 0,
                                                        })
                                                    }
                                                    style={{marginTop: 4}}
                                                >
                                                    افزودن بازه
                                                </Button>
                                            </div>
                                        )}
                                    </Form.List>

                                    <Space style={{marginTop: 12}}>
                                        <Button type="primary" size="small" loading={saving}
                                                onClick={() => form.submit()}>
                                            ذخیره
                                        </Button>
                                        <Button size="small" onClick={() => setEditing(null)}>
                                            انصراف
                                        </Button>
                                    </Space>
                                </Form>
                            )}
                        </Card>
                    );
                })
            )}
        </Card>
    );
}
